/**
 * The world tidying up after itself: anything that has fallen out of it is put back where
 * the player last had it (design D9).
 *
 * The grass collider is exactly the maze plus its apron, and the moat's water is drawn but
 * never collidable — so a leg, the screwdriver or an assembled dog thrown over the bank
 * falls past the water and keeps falling. Before this, that was the end of it: the part was
 * gone and the run was unfinishable.
 *
 * A sweep rather than a kill-plane sensor, because the recovery is a body mutation and a
 * sensor would fire it from inside Rapier's own intersection dispatch. A `useFrame` runs
 * between physics steps, where moving a body is safe.
 *
 * The three conditions on a body are each load-bearing. **Enabled**: a stowed item and an
 * attached leg are parked at the origin and switched off, which is below the plane, and
 * recovering one would spill the pocket onto the lawn. **Dynamic**: a carried object, a
 * walking dog (`COMPANION_GROUPS`) and a table are all kinematic, and none of them can
 * fall. **Below the plane**: everything else is in play. What is left is exactly the set of
 * things that have left the world — including the robot dog's torso, which `RobotDog.tsx`
 * could not return on its own.
 */

import { useFrame } from '@react-three/fiber';
import { useRapier } from '@react-three/rapier';
import { useEffect } from 'react';
import { hasLeftTheWorld } from '../game/bounds';
import type { Rect } from '../game/profile/types';
import { clearObjectHomes, objectHome, setHomeLawn } from './objectHomes';
import { forEachObjectBody } from './objectRegistry';

const ZERO = { x: 0, y: 0, z: 0 };

type Props = {
  /** This level's grass, or null indoors — see `recordObjectHome` for what it decides. */
  lawn: Rect | null | undefined;
};

export function LostAndFound({ lawn }: Props): null {
  const { rapier } = useRapier();

  useEffect(() => {
    setHomeLawn(lawn);
  }, [lawn]);

  // Separate from the lawn above, and deliberately mount-scoped: this component sits inside
  // `App`'s group keyed on the level id, so one unmount is exactly one level going away,
  // and a pose in the last level means nothing in this one.
  useEffect(() => clearObjectHomes, []);

  useFrame(() => {
    forEachObjectBody((id, body) => {
      if (!body.isEnabled()) return;
      if (body.bodyType() !== rapier.RigidBodyType.Dynamic) return;
      if (!hasLeftTheWorld(body.translation().y)) return;

      const home = objectHome(id);
      // Nothing recorded means nothing to return it to. Leaving it falling is what the
      // game did before this component existed, and is better than guessing at a pose.
      if (!home) return;

      // The sequence `parkBody` uses, without the switch-off: pose first, then stilled, so
      // it arrives at rest rather than carrying the fall's velocity into the lawn.
      body.setTranslation(home.position, true);
      body.setRotation(home.rotation, true);
      body.setLinvel(ZERO, true);
      body.setAngvel(ZERO, true);
    });
  });

  return null;
}
